import invariant from 'invariant';

import eventEmitter, { SVG_FILL } from 'Lib/utils/emitter';

import { debugSvgFillManager as debug } from '../debug';
import type { AddBatchValues, Fill, FillMap, FillType, UnsanitizedFillPropsMap } from './types';
import { sanitizeFillParams } from './utils';

const sanitizeKey = (key: string) => key.replace(/\s/g, '-');

/**
 * SVG Fill Manager
 * ================
 *
 * SVGs can use gradient or pattern fills when they can be referenced by ID. This manager faciliates
 * registration of fill patterns so that they can be referenced by ID later on. Fills are assigned
 * unique IDs under the hood to prevent collisions.
 */
export default class SvgFillManager {
  idPrefix: string;
  _fills: Map<string, Fill> = new Map();

  constructor(idPrefix: string = 'svg-fill') {
    this.idPrefix = idPrefix;
  }

  addFill<TFillType extends FillType, TFill extends FillMap[TFillType]>(
    key: string,
    type: TFillType,
    params: UnsanitizedFillPropsMap[TFillType]
  ) {
    const fillKey = sanitizeKey(key);
    const props = sanitizeFillParams(params, type);
    const id = `${this.idPrefix}-${type}__${fillKey}`;
    const fill = { key, id, props, type };
    if (this._fills.has(fillKey)) {
      debug(`modifying existing fill '${fillKey}'`);
    }
    this._fills.set(fillKey, fill as unknown as TFill);
    return fillKey;
  }

  /**
   * Registers a fill
   *
   * @param type - fill type
   * @param params - fill parameters
   * @param userFillKey - fill key
   */
  add<TFillType extends FillType>(
    key: string,
    type: FillType,
    params: UnsanitizedFillPropsMap[TFillType]
  ): string {
    const fillKey = this.addFill(key, type, params);
    this.dispatch();
    return fillKey;
  }

  addBatch(values: AddBatchValues) {
    const fillKeys: Record<string, string> = {};
    Object.entries(values).forEach(([key, { type, params }]) => {
      fillKeys[key] = this.addFill(key, type, params);
    });
    this.dispatch();
    return fillKeys;
  }

  /**
   * Removes a fill by key
   *
   * @param fillKey - fill key
   */
  remove(key: string | string[]) {
    const keys = typeof key === 'string' ? [key] : key;

    let needsDispatch = false;
    keys.forEach((rawKey) => {
      const fillKey = sanitizeKey(rawKey);
      if (this._fills.has(fillKey)) {
        this._fills.delete(fillKey);
        needsDispatch = true;
      }
    });

    if (needsDispatch) {
      this.dispatch();
    }
  }

  /**
   * Determines whether a fill exists for the given key
   *
   * @param fillKey - fill key
   * @return whether or not a fill with the specified key exists
   */
  has(key: string): boolean {
    return this._fills.has(sanitizeKey(key));
  }

  /**
   * Returns the fill URL for the given key
   *
   * @param fillKey - fill key
   * @return fill URL
   */
  get(fillKey: string): string | null {
    const key = sanitizeKey(fillKey);
    const fill = this._fills.get(key);
    const fillId = fill?.id;
    if (!fillId) {
      debug(`no fill for ${key}`);
      return null;
    }
    return `url(#${fillId})`;
  }

  /**
   * Gets all registered fills
   */
  getFills() {
    const patterns = {};
    this._fills.forEach((pattern, key) => {
      patterns[key] = pattern;
    });
    return patterns;
  }

  /**
   * Notify all listeners of a potential change
   */
  dispatch() {
    eventEmitter.emit(SVG_FILL);
  }

  /**
   * Subscribe to potential fill changes
   *
   * @param listener - invoked when a gradient is added or removed
   * @return unsubscribe function to remove the listener
   */
  subscribe(listener: VoidFunction): VoidFunction {
    invariant(typeof listener === 'function', 'Must supply a listener function');
    eventEmitter.on(SVG_FILL, listener);

    return function unsubscribe() {
      eventEmitter.removeListener(SVG_FILL, listener);
    };
  }
}

export {
  FILL_TYPE_ANGULAR_GRADIENT,
  FILL_TYPE_IMAGE,
  FILL_TYPE_LINEAR_GRADIENT,
  FILL_TYPE_RADIAL_GRADIENT,
  SPREAD_METHOD_PAD,
  SPREAD_METHOD_REFLECT,
  SPREAD_METHOD_REPEAT,
} from './constants';
